import type { StripeElements } from '@stripe/stripe-js';

import { keepVisibleAboveKeyboard } from '#src/app/shared/utils/dom';

import type { ValidationErrors } from './stripeInputValidationError';

export type StripeFocusElementType = 'card' | 'cardNumber' | 'cardExpiry' | 'cardCvc' | 'iban';

const PROGRAMMATIC_FOCUS_WINDOW_MS = 1000;

const ERROR_MESSAGE_ALLOWANCE_PX = 64;

export function stripeElementDomId(elementType: StripeFocusElementType): string {
	return `stripe-element-${elementType}`;
}

const programmaticFocusAtByType: Partial<Record<StripeFocusElementType, number>> = {};

/**
 * Returns true when a focus event on `elementType` follows a programmatic focus of that same element,
 * so focus analytics can ignore it and only count user-initiated focus.
 */
export function consumeProgrammaticStripeFocus(elementType: StripeFocusElementType): boolean {
	const focusedAt = programmaticFocusAtByType[elementType];
	const isProgrammatic = focusedAt !== undefined && Date.now() - focusedAt < PROGRAMMATIC_FOCUS_WINDOW_MS;
	delete programmaticFocusAtByType[elementType];
	return isProgrammatic;
}

export function guardStripeFocusTracker(onFieldFocus: () => void, elementType: StripeFocusElementType): () => void {
	return () => {
		if (consumeProgrammaticStripeFocus(elementType)) return;
		onFieldFocus();
	};
}

function getStripeElement(elements: StripeElements, elementType: StripeFocusElementType): { focus: () => void } | null {
	switch (elementType) {
		case 'cardNumber':
			return elements.getElement('cardNumber');
		case 'cardExpiry':
			return elements.getElement('cardExpiry');
		case 'cardCvc':
			return elements.getElement('cardCvc');
		case 'iban':
			return elements.getElement('iban');
		case 'card':
		default:
			return elements.getElement('card');
	}
}

export function focusStripeElement(
	elements: StripeElements | null,
	elementType: StripeFocusElementType,
): StripeFocusElementType | null {
	if (!elements) return null;

	let element = getStripeElement(elements, elementType);
	let focusedType = elementType;
	if (!element && elementType !== 'card') {
		element = getStripeElement(elements, 'card');
		focusedType = 'card';
	}
	if (!element) return null;

	programmaticFocusAtByType[focusedType] = Date.now();
	element.focus();
	return focusedType;
}

export function focusInvalidStripeElement(elements: StripeElements | null, validationErrors: ValidationErrors): void {
	if (!elements) return;

	let elementType: StripeFocusElementType | null = null;
	if (validationErrors.card_number) elementType = 'cardNumber';
	else if (validationErrors.card_expiration) elementType = 'cardExpiry';
	else if (validationErrors.card_cvc) elementType = 'cardCvc';
	else if (validationErrors.account_number) elementType = 'iban';
	else if (validationErrors.card) elementType = 'card';

	if (!elementType) return;
	const focusedType = focusStripeElement(elements, elementType);
	if (!focusedType) return;

	const container = document.getElementById(stripeElementDomId(focusedType));
	if (!container) return;
	container.scrollIntoView({ block: 'center' });
	keepVisibleAboveKeyboard(container, { bottomAllowancePx: ERROR_MESSAGE_ALLOWANCE_PX });
}
